import { Box, Table } from '@zillow/constellation';
import React from 'react';

import type { ZestimateTableProps } from './ZestimateTable.types';

// Re-export types for convenience
export type {
    ZestimateTableData,
    ZestimateTableProps,
} from './ZestimateTable.types';

/**
 * ZestimateTable component that displays accuracy data in a responsive table.
 * Columns beyond the first 2 are hidden on tablet and mobile devices.
 *
 * @component
 * @example
 * ```tsx
 * <ZestimateTable data={tableData} ariaLabel="Zestimate accuracy data" />
 * ```
 */
export function ZestimateTable({
    data,
    ariaLabel,
    className,
}: ZestimateTableProps): JSX.Element {
    const { headers, data: rows } = data;
    const columnWidth = `${75 / headers.length - 1}%`;

    const superscriptedHeaders = ['Median Error', 'Homes With Zestimates'];
    const superscriptCountByIndex = headers.map((header) => {
        if (superscriptedHeaders.includes(header)) {
            return superscriptedHeaders.indexOf(header) + 1;
        }
        return 0;
    });

    const headerSuperscript = (index: number) => {
        const count = superscriptCountByIndex[index];
        return count > 0 ? <sup>{'*'.repeat(count)}</sup> : null;
    };

    return (
        <Box className={className}>
            <Table.Responsive>
                <Table size="sm" appearance="zebra" aria-label={ariaLabel}>
                    <Table.Header>
                        <Table.Row>
                            {headers.map((header, index) => {
                                return (
                                    <Box
                                        as={Table.HeaderCell}
                                        key={index}
                                        sx={{
                                            width:
                                                index === 0
                                                    ? '25%'
                                                    : columnWidth,
                                            '@media $md_lte': {
                                                display:
                                                    index > 1
                                                        ? 'none'
                                                        : 'table-cell',
                                            },
                                        }}
                                    >
                                        <span>
                                            {header}
                                            {headerSuperscript(index)}
                                        </span>
                                    </Box>
                                );
                            })}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {rows.map((row, rowIndex) => (
                            <Table.Row key={rowIndex}>
                                {Object.values(row).map((cell, cellIndex) => {
                                    return (
                                        <Box
                                            as={Table.Cell}
                                            key={cellIndex}
                                            sx={{
                                                width:
                                                    cellIndex === 0
                                                        ? '25%'
                                                        : columnWidth,
                                                '@media $md_lte': {
                                                    display:
                                                        cellIndex > 1
                                                            ? 'none'
                                                            : 'table-cell',
                                                },
                                            }}
                                        >
                                            {cell}
                                        </Box>
                                    );
                                })}
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </Table.Responsive>
        </Box>
    );
}
